import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  BriefcaseBusiness,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock3,
  LoaderCircle,
  Plus,
  Search,
  ShieldCheck,
  Trash2,
  UserRound,
  UsersRound,
  X,
} from "lucide-react";
import UsersList from "./UsersList";
import Grouproleschema from "./Grouproleschema";
import { apiFetch } from "../utils/apiClient";

const MANAGED_FORMS = [
  { key: "ReclamationDepense", name: "Réclamation mensuelle de dépenses", processingGroup: "Réclamation mensuelle de dépenses" },
  { key: "Adresse", name: "Changement d'adresse", processingGroup: "Changement adresse" },
  { key: "Nom", name: "Changement de nom", processingGroup: "Changement de nom" },
  { key: "InfoBancaire", name: "Changement informations bancaires", processingGroup: "Changement information bancaire" },
  { key: "ReseauInfo", name: "Compte informatique temporaire", processingGroup: "Accès au compte informatique" },
  { key: "AntecedentCriminel", name: "Antécédents criminels", processingGroup: "Antécédents criminels" },
  { key: "AchatEI", name: "Achat équipement informatique", processingGroup: "Consultation Achat équipement informatique" },
  { key: "EntenteTravailHybrid", name: "Entente de travail en mode hybride", processingGroup: "Entente de travail en mode hybrid" },
  { key: "AccesHP", name: "Accès hors pays", processingGroup: null },
  { key: "DemandeProjet", name: "Demande de projet", processingGroup: null },
  {
    key: "SeancePlacement",
    name: "Séance de placement — AEFO, FEESO et SCFP",
    processingGroup: "Séance de placement",
    accessManagedSeparately: true,
  },
];

const ACCESS_TYPES = [
  { key: "user", label: "Personnes", singularLabel: "Personne", icon: UserRound },
  { key: "jobTitle", label: "Titres de poste", singularLabel: "Titre de poste", icon: BriefcaseBusiness },
  { key: "entraGroup", label: "Groupes Entra", singularLabel: "Groupe Entra", icon: UsersRound },
];

const DETAIL_TABS = [
  { key: "access", label: "Accès", icon: ShieldCheck },
  { key: "history", label: "Historique", icon: Clock3 },
];

const createEmptyConfiguration = (form) => ({
  formKey: form.key,
  formName: form.name,
  active: true,
  allowedUsers: [],
  allowedJobTitles: [],
  allowedEntraGroups: [],
});

const buildConfigurations = (savedConfigurations = []) => {
  const configurations = Object.fromEntries(
    MANAGED_FORMS.map((form) => [form.key, createEmptyConfiguration(form)]),
  );

  savedConfigurations.forEach((configuration) => {
    if (configurations[configuration.formKey]) {
      configurations[configuration.formKey] = {
        ...configurations[configuration.formKey],
        ...configuration,
      };
    }
  });

  return configurations;
};

const getAccessCounts = (configuration) => ({
  users: configuration.allowedUsers.length,
  jobTitles: configuration.allowedJobTitles.length,
  groups: configuration.allowedEntraGroups.length,
  total:
    configuration.allowedUsers.length +
    configuration.allowedJobTitles.length +
    configuration.allowedEntraGroups.length,
});

const getAccessSummary = (configuration) => {
  const counts = getAccessCounts(configuration);
  const parts = [];

  if (counts.users > 0) {
    parts.push(`${counts.users} personne${counts.users > 1 ? "s" : ""}`);
  }
  if (counts.jobTitles > 0) {
    parts.push(`${counts.jobTitles} titre${counts.jobTitles > 1 ? "s" : ""} de poste`);
  }
  if (counts.groups > 0) {
    parts.push(`${counts.groups} groupe${counts.groups > 1 ? "s" : ""} Entra`);
  }

  return parts.length > 0 ? parts.join(" · ") : "Aucun accès défini";
};

const formatAuditDateTime = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat("fr-CA", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const ACCESS_TYPE_LABELS = {
  user: "Personne",
  jobTitle: "Titre de poste",
  entraGroup: "Groupe Entra",
};

const FormAccessManager = ({
  defaultOpen = false,
  processingRoles = [],
  onProcessingRoleAdded,
  onProcessingRoleDeleted,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [configurations, setConfigurations] = useState(() => buildConfigurations());
  const [selectedFormKey, setSelectedFormKey] = useState(MANAGED_FORMS[0].key);
  const [highlightedFormKey, setHighlightedFormKey] = useState(null);
  const [view, setView] = useState("list");
  const [formSearch, setFormSearch] = useState("");
  const [unconfiguredOpen, setUnconfiguredOpen] = useState(false);
  const [showAddAccess, setShowAddAccess] = useState(false);
  const [detailTab, setDetailTab] = useState("access");
  const [panelSection, setPanelSection] = useState("formAccess");
  const [showAddProcessingMember, setShowAddProcessingMember] = useState(false);
  const [selectedProcessingUser, setSelectedProcessingUser] = useState(null);
  const [processingUserSearchKey, setProcessingUserSearchKey] = useState(0);
  const [processingSaving, setProcessingSaving] = useState(false);
  const [processingMessage, setProcessingMessage] = useState(null);
  const [accessType, setAccessType] = useState("user");
  const [selectedUser, setSelectedUser] = useState(null);
  const [userSearchKey, setUserSearchKey] = useState(0);
  const [jobTitle, setJobTitle] = useState("");
  const [groupQuery, setGroupQuery] = useState("");
  const [groupResults, setGroupResults] = useState([]);
  const [loadingConfigurations, setLoadingConfigurations] = useState(true);
  const [searchingGroups, setSearchingGroups] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [historyEntries, setHistoryEntries] = useState([]);
  const [historyLoadedFor, setHistoryLoadedFor] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState("");

  const selectedConfiguration = configurations[selectedFormKey];
  const selectedForm = MANAGED_FORMS.find((form) => form.key === selectedFormKey);
  const selectedCounts = getAccessCounts(selectedConfiguration);
  const processingRolesByGroup = useMemo(
    () =>
      processingRoles.reduce((groups, role) => {
        if (!groups[role.group]) groups[role.group] = [];
        groups[role.group].push(role);
        return groups;
      }, {}),
    [processingRoles],
  );
  const selectedProcessingRoles = selectedForm?.processingGroup
    ? processingRolesByGroup[selectedForm.processingGroup] || []
    : [];

  const filteredForms = useMemo(() => {
    const normalizedSearch = formSearch.trim().toLocaleLowerCase("fr-CA");

    return MANAGED_FORMS.filter(
      (form) =>
        !normalizedSearch ||
        form.name.toLocaleLowerCase("fr-CA").includes(normalizedSearch),
    );
  }, [formSearch]);

  const configuredForms = filteredForms.filter(
    (form) =>
      form.accessManagedSeparately ||
      getAccessCounts(configurations[form.key]).total > 0,
  );
  const unconfiguredForms = filteredForms.filter(
    (form) =>
      !form.accessManagedSeparately &&
      getAccessCounts(configurations[form.key]).total === 0,
  );

  const detailRows = useMemo(
    () => [
      ...selectedConfiguration.allowedUsers.map((user) => ({
        type: "user",
        identifier: user.userId,
        title: user.displayName,
        detail: [user.email, user.jobTitle].filter(Boolean).join(" · "),
        label: "Personne",
      })),
      ...selectedConfiguration.allowedJobTitles.map((title) => ({
        type: "jobTitle",
        identifier: title,
        title,
        detail: "Toutes les personnes ayant exactement ce titre",
        label: "Titre de poste",
      })),
      ...selectedConfiguration.allowedEntraGroups.map((group) => ({
        type: "entraGroup",
        identifier: group.objectId,
        title: group.displayName,
        detail: "",
        label: "Groupe Entra",
      })),
    ],
    [selectedConfiguration],
  );

  const resetAccessEditor = useCallback(() => {
    setSelectedUser(null);
    setJobTitle("");
    setGroupQuery("");
    setGroupResults([]);
    setMessage(null);
    setUserSearchKey((value) => value + 1);
  }, []);

  const resetProcessingEditor = useCallback(() => {
    setSelectedProcessingUser(null);
    setProcessingMessage(null);
    setProcessingUserSearchKey((value) => value + 1);
  }, []);

  const openFormDetails = (formKey) => {
    const form = MANAGED_FORMS.find((item) => item.key === formKey);
    setSelectedFormKey(formKey);
    setHighlightedFormKey(formKey);
    setView("detail");
    setShowAddAccess(true);
    setShowAddProcessingMember(true);
    setPanelSection(
      form?.accessManagedSeparately ? "processingGroup" : "formAccess",
    );
    setDetailTab("access");
    setHistoryEntries([]);
    setHistoryLoadedFor(null);
    setHistoryError("");
    resetAccessEditor();
    resetProcessingEditor();
  };

  const closeFormDetails = useCallback(() => {
    setView("list");
    setShowAddAccess(false);
    setShowAddProcessingMember(false);
    resetAccessEditor();
    resetProcessingEditor();
  }, [resetAccessEditor, resetProcessingEditor]);

  useEffect(() => {
    const controller = new AbortController();
    const loadConfigurations = async () => {
      setLoadingConfigurations(true);
      try {
        const response = await apiFetch(
          `${process.env.REACT_APP_API_BASE_URL}/formAccessConfigurations`,
          { signal: controller.signal },
        );
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || "Impossible de charger les accès.");
        setConfigurations(buildConfigurations(data));
      } catch (error) {
        if (error.name !== "AbortError") setMessage({ type: "error", text: error.message });
      } finally {
        if (!controller.signal.aborted) setLoadingConfigurations(false);
      }
    };
    loadConfigurations();
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (accessType !== "entraGroup" || groupQuery.trim().length < 2) {
      setGroupResults((currentResults) =>
        currentResults.length > 0 ? [] : currentResults,
      );
      setSearchingGroups(false);
      return undefined;
    }

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setSearchingGroups(true);
      setMessage(null);
      try {
        const response = await apiFetch(
          `${process.env.REACT_APP_API_BASE_URL}/searchEntraGroups?q=${encodeURIComponent(groupQuery.trim())}`,
          { signal: controller.signal },
        );
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || "Impossible de rechercher les groupes Entra.");
        setGroupResults(data);
      } catch (error) {
        if (error.name !== "AbortError") {
          setGroupResults([]);
          setMessage({ type: "error", text: error.message });
        }
      } finally {
        if (!controller.signal.aborted) setSearchingGroups(false);
      }
    }, 350);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [accessType, groupQuery]);

  // Charge l'historique seulement lorsque l'administrateur ouvre l'onglet.
  // Une consultation normale de la page ne fait donc aucun appel inutile.
  useEffect(() => {
    if (
      view !== "detail" ||
      detailTab !== "history" ||
      historyLoadedFor === selectedFormKey
    ) {
      return undefined;
    }

    const controller = new AbortController();

    const loadHistory = async () => {
      setLoadingHistory(true);
      setHistoryError("");

      try {
        const response = await apiFetch(
          `${process.env.REACT_APP_API_BASE_URL}/formAccessConfigurations/${selectedFormKey}/history`,
          { signal: controller.signal },
        );
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || "Impossible de charger l'historique.");
        }

        setHistoryEntries(data);
        setHistoryLoadedFor(selectedFormKey);
      } catch (error) {
        if (error.name !== "AbortError") {
          setHistoryEntries([]);
          setHistoryError(error.message);
        }
      } finally {
        if (!controller.signal.aborted) setLoadingHistory(false);
      }
    };

    loadHistory();
    return () => controller.abort();
  }, [detailTab, historyLoadedFor, selectedFormKey, view]);

  // Pendant que le panneau est ouvert, le défilement de la page principale
  // est bloqué. Seul le contenu du panneau peut défiler.
  useEffect(() => {
    if (view !== "detail") return undefined;

    const scrollY = window.scrollY;
    const previousBodyStyles = {
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      top: document.body.style.top,
      left: document.body.style.left,
      right: document.body.style.right,
      width: document.body.style.width,
    };
    const previousHtmlOverflow = document.documentElement.style.overflow;

    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";

    const handleEscape = (event) => {
      if (event.key === "Escape") closeFormDetails();
    };
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.documentElement.style.overflow = previousHtmlOverflow;
      Object.assign(document.body.style, previousBodyStyles);
      window.scrollTo(0, scrollY);
    };
  }, [closeFormDetails, view]);

  const saveConfiguration = async (nextConfiguration, successText) => {
    setSaving(true);
    setMessage(null);
    try {
      const response = await apiFetch(
        `${process.env.REACT_APP_API_BASE_URL}/formAccessConfigurations/${selectedFormKey}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(nextConfiguration),
        },
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Impossible d'enregistrer les accès.");
      setConfigurations((current) => ({ ...current, [selectedFormKey]: data }));
      setHistoryLoadedFor(null);
      setMessage({ type: "success", text: successText });
      return true;
    } catch (error) {
      setMessage({ type: "error", text: error.message });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const finishSuccessfulAdd = () => {
    setDetailTab("access");
    setShowAddAccess(true);
  };

  const addSelectedUser = async () => {
    if (!selectedUser || saving) return;
    if (selectedConfiguration.allowedUsers.some((user) => user.userId === selectedUser.id)) {
      setMessage({ type: "error", text: "Cette personne est déjà ajoutée." });
      return;
    }
    const nextConfiguration = {
      ...selectedConfiguration,
      allowedUsers: [...selectedConfiguration.allowedUsers, {
        userId: selectedUser.id,
        displayName: selectedUser.displayName,
        email: selectedUser.mail || selectedUser.userPrincipalName || "",
        jobTitle: selectedUser.jobTitle || "",
      }],
    };
    if (await saveConfiguration(nextConfiguration, "Personne ajoutée avec succès.")) {
      setSelectedUser(null);
      setUserSearchKey((value) => value + 1);
      finishSuccessfulAdd();
    }
  };

  const addJobTitle = async () => {
    const normalizedTitle = jobTitle.trim();
    if (!normalizedTitle || saving) return;
    if (selectedConfiguration.allowedJobTitles.some(
      (title) => title.toLocaleLowerCase("fr-CA") === normalizedTitle.toLocaleLowerCase("fr-CA"),
    )) {
      setMessage({ type: "error", text: "Ce titre de poste est déjà ajouté." });
      return;
    }
    const nextConfiguration = {
      ...selectedConfiguration,
      allowedJobTitles: [...selectedConfiguration.allowedJobTitles, normalizedTitle],
    };
    if (await saveConfiguration(nextConfiguration, "Titre de poste ajouté avec succès.")) {
      setJobTitle("");
      finishSuccessfulAdd();
    }
  };

  const addEntraGroup = async (group) => {
    if (saving) return;
    if (selectedConfiguration.allowedEntraGroups.some(
      (savedGroup) => savedGroup.objectId === group.objectId,
    )) {
      setMessage({ type: "error", text: "Ce groupe est déjà ajouté." });
      return;
    }
    const nextConfiguration = {
      ...selectedConfiguration,
      allowedEntraGroups: [...selectedConfiguration.allowedEntraGroups, {
        objectId: group.objectId,
        displayName: group.displayName,
      }],
    };
    if (await saveConfiguration(nextConfiguration, "Groupe ajouté avec succès.")) {
      setGroupQuery("");
      setGroupResults([]);
      finishSuccessfulAdd();
    }
  };

  const removeAccess = async (type, identifier) => {
    if (saving) return;
    let nextConfiguration = selectedConfiguration;
    if (type === "user") {
      nextConfiguration = { ...selectedConfiguration, allowedUsers: selectedConfiguration.allowedUsers.filter(
        (user) => user.userId !== identifier,
      ) };
    } else if (type === "jobTitle") {
      nextConfiguration = { ...selectedConfiguration, allowedJobTitles: selectedConfiguration.allowedJobTitles.filter(
        (title) => title !== identifier,
      ) };
    } else {
      nextConfiguration = { ...selectedConfiguration, allowedEntraGroups: selectedConfiguration.allowedEntraGroups.filter(
        (group) => group.objectId !== identifier,
      ) };
    }
    await saveConfiguration(nextConfiguration, "Accès retiré avec succès.");
  };

  const addProcessingMember = async () => {
    if (
      !selectedForm.processingGroup ||
      !selectedProcessingUser ||
      processingSaving
    ) {
      return;
    }

    const alreadyExists = selectedProcessingRoles.some(
      (role) => role.userId === selectedProcessingUser.id,
    );
    if (alreadyExists) {
      setProcessingMessage({
        type: "error",
        text: "Cette personne est déjà membre du groupe de traitement.",
      });
      return;
    }

    setProcessingSaving(true);
    setProcessingMessage(null);
    try {
      await onProcessingRoleAdded?.({
        userId: selectedProcessingUser.id,
        email:
          selectedProcessingUser.mail ||
          selectedProcessingUser.userPrincipalName ||
          "",
        nom: selectedProcessingUser.displayName,
        group: selectedForm.processingGroup,
      });
      setProcessingMessage({
        type: "success",
        text: "Membre ajouté au groupe de traitement.",
      });
      setSelectedProcessingUser(null);
      setProcessingUserSearchKey((value) => value + 1);
    } catch (error) {
      setProcessingMessage({
        type: "error",
        text: error.message || "Impossible d'ajouter ce membre.",
      });
    } finally {
      setProcessingSaving(false);
    }
  };

  const renderAdministrativeRow = (form, configured) => {
    const configuration = configurations[form.key];
    const selected = highlightedFormKey === form.key;
    const accessManagedSeparately = Boolean(form.accessManagedSeparately);
    const processingCount = form.processingGroup
      ? (processingRolesByGroup[form.processingGroup] || []).length
      : null;

    return (
      <button
        key={form.key}
        type="button"
        onClick={() => openFormDetails(form.key)}
        className={`group grid w-full grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 border-l-4 px-4 py-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500 sm:grid-cols-[auto_minmax(0,1fr)_9rem_12rem_auto] sm:px-5 ${
          selected
            ? "border-blue-600 bg-blue-50"
            : "border-transparent hover:bg-blue-50/60"
        }`}
        aria-current={selected ? "true" : undefined}
      >
        <span
          className={`inline-flex h-9 w-9 flex-none items-center justify-center rounded-lg ${
            configured
              ? "bg-blue-50 text-blue-700"
              : "bg-slate-100 text-slate-500"
          }`}
        >
          <ShieldCheck className="h-4 w-4" aria-hidden="true" />
        </span>

        <span className="min-w-0 flex-1">
          <span className="block break-words text-sm font-semibold text-slate-900 group-hover:text-blue-800">
            {form.name}
          </span>
          <span
            className={`mt-1 block text-xs ${
              configured ? "text-slate-600" : "text-slate-500"
            }`}
          >
            {accessManagedSeparately
              ? "Accès gérés séparément"
              : getAccessSummary(configuration)}
          </span>
          <span className="mt-2 flex flex-wrap items-center gap-2 sm:hidden">
            <span
              className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                accessManagedSeparately
                  ? "bg-slate-100 text-slate-700"
                  : configured
                    ? "bg-emerald-50 text-emerald-700"
                    : "bg-amber-50 text-amber-700"
              }`}
            >
              {accessManagedSeparately
                ? "Accès séparés"
                : configured
                  ? "Configuré"
                  : "À configurer"}
            </span>
            <span className="text-[11px] font-medium text-slate-500">
              {processingCount === null
                ? "Traitement selon la demande"
                : `${processingCount} membre${processingCount > 1 ? "s" : ""} traitant${processingCount > 1 ? "s" : ""}`}
            </span>
          </span>
        </span>

        <span className="hidden sm:block">
          <span
            className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
              accessManagedSeparately
                ? "bg-slate-100 text-slate-700"
                : configured
                  ? "bg-emerald-50 text-emerald-700"
                  : "bg-amber-50 text-amber-700"
            }`}
          >
            {accessManagedSeparately
              ? "Accès séparés"
              : configured
                ? "Configuré"
                : "À configurer"}
          </span>
        </span>

        <span className="hidden text-sm font-medium text-slate-600 sm:block">
          {processingCount === null
            ? "Selon la demande"
            : `${processingCount} membre${processingCount > 1 ? "s" : ""}`}
        </span>

        <ChevronRight className="h-5 w-5 flex-none text-slate-400 transition-transform group-hover:translate-x-0.5 group-hover:text-blue-600" />
      </button>
    );
  };

  const renderMessage = () => message && (
    <div
      className={`flex items-start gap-2 rounded-lg border px-3 py-3 text-sm ${
        message.type === "success"
          ? "border-emerald-200 bg-emerald-50 text-emerald-800"
          : "border-red-200 bg-red-50 text-red-800"
      }`}
      role={message.type === "error" ? "alert" : "status"}
    >
      {message.type === "success" ? (
        <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none" />
      ) : (
        <AlertCircle className="mt-0.5 h-4 w-4 flex-none" />
      )}
      <span>{message.text}</span>
    </div>
  );

  const renderAddAccess = () => (
    <div className="rounded-xl border border-blue-200 bg-blue-50/40 p-4 sm:p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h4 className="text-base font-semibold text-slate-900">Ajouter un accès</h4>
          <p className="mt-1 text-sm text-slate-600">Choisissez comment cette autorisation doit être accordée.</p>
        </div>
        <button
          type="button"
          onClick={() => setShowAddAccess((current) => !current)}
          className="flex-none text-sm font-semibold text-blue-700 hover:text-blue-900"
          aria-expanded={showAddAccess}
        >
          {showAddAccess ? "Réduire" : "Afficher"}
        </button>
      </div>

      {showAddAccess && (
      <>
      <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-3">
        {ACCESS_TYPES.map((type) => {
          const Icon = type.icon;
          const selected = accessType === type.key;
          return (
            <button
              key={type.key}
              type="button"
              onClick={() => { setAccessType(type.key); setMessage(null); }}
              disabled={saving}
              className={`inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:opacity-60 ${
                selected
                  ? "border-blue-600 bg-white text-blue-700 shadow-sm"
                  : "border-slate-300 bg-white/70 text-slate-700 hover:bg-white"
              }`}
              aria-pressed={selected}
            >
              <Icon className="h-4 w-4" aria-hidden="true" />
              {type.singularLabel}
            </button>
          );
        })}
      </div>

      <div className="mt-4 rounded-lg border border-slate-200 bg-white p-4">
        {accessType === "user" && (
          <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
            <div className="min-w-0">
              <label className="mb-1.5 block text-sm font-semibold text-slate-800">Rechercher une personne</label>
              <UsersList
                key={`${selectedFormKey}-${userSearchKey}`}
                onUserSelect={setSelectedUser}
                initialValue={selectedUser?.mail || selectedUser?.userPrincipalName || ""}
                isRequired={false}
                placeholder="Nom ou courriel"
                inputClassName="min-h-11 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-800 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              />
              {selectedUser && (
                <p className="mt-2 text-xs text-slate-600">
                  {selectedUser.displayName}{selectedUser.jobTitle ? ` · ${selectedUser.jobTitle}` : ""}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={addSelectedUser}
              disabled={!selectedUser || saving}
              className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50 lg:w-auto"
            >
              {saving ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Ajouter
            </button>
          </div>
        )}

        {accessType === "jobTitle" && (
          <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
            <div>
              <label htmlFor="form-access-job-title" className="mb-1.5 block text-sm font-semibold text-slate-800">
                Titre de poste exact
              </label>
              <input
                id="form-access-job-title"
                type="text"
                value={jobTitle}
                onChange={(event) => setJobTitle(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") { event.preventDefault(); addJobTitle(); }
                }}
                placeholder="Exemple : _Programmeur"
                disabled={saving}
                className="min-h-11 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-800 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:opacity-60"
              />
            </div>
            <button
              type="button"
              onClick={addJobTitle}
              disabled={!jobTitle.trim() || saving}
              className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50 lg:w-auto"
            >
              {saving ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Ajouter
            </button>
          </div>
        )}

        {accessType === "entraGroup" && (
          <div>
            <label htmlFor="form-access-entra-group" className="mb-1.5 block text-sm font-semibold text-slate-800">
              Rechercher un groupe de sécurité
            </label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
              <input
                id="form-access-entra-group"
                type="search"
                value={groupQuery}
                onChange={(event) => setGroupQuery(event.target.value)}
                placeholder="Saisir au moins deux caractères"
                disabled={saving}
                className="min-h-11 w-full rounded-lg border border-slate-300 bg-white py-2.5 pl-9 pr-10 text-sm text-slate-800 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:opacity-60"
              />
              {searchingGroups && <LoaderCircle className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-blue-700" />}
            </div>

            {groupResults.length > 0 && (
              <div className="mt-3 max-h-64 divide-y divide-slate-200 overflow-y-auto rounded-lg border border-slate-200">
                {groupResults.map((group) => (
                  <div key={group.objectId} className="flex flex-col gap-3 bg-white px-3 py-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <p className="break-words text-sm font-semibold text-slate-900">{group.displayName}</p>
                      <p className="mt-0.5 break-all text-xs text-slate-500">
                        {group.description || group.email || "Groupe de sécurité Microsoft Entra"}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => addEntraGroup(group)}
                      disabled={saving}
                      className="inline-flex min-h-10 w-full flex-none items-center justify-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100 disabled:opacity-50 sm:w-auto"
                    >
                      <Plus className="h-4 w-4" /> Ajouter
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      {message && <div className="mt-4">{renderMessage()}</div>}
      </>
      )}
    </div>
  );

  return (
    <section className="mb-10 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <button
        type="button"
        onClick={() => setIsOpen((current) => !current)}
        className="flex w-full items-center justify-between gap-4 px-4 py-4 text-left transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500 sm:px-6"
        aria-expanded={isOpen}
        aria-controls="form-access-manager-content"
      >
        <span className="flex min-w-0 items-start gap-3">
          <span className="inline-flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-blue-50 text-blue-700">
            <ShieldCheck className="h-5 w-5" aria-hidden="true" />
          </span>
          <span className="min-w-0">
            <span className="block text-base font-semibold text-slate-900 sm:text-lg">Gestion des formulaires</span>
            <span className="mt-1 block text-xs text-slate-500 sm:text-sm">
              Gérez les accès aux formulaires et leurs groupes de traitement.
            </span>
          </span>
        </span>
        <span className="flex flex-none items-center gap-2 text-sm font-semibold text-slate-700">
          <span className="hidden sm:inline">{isOpen ? "Réduire" : "Gérer les formulaires"}</span>
          <ChevronDown className={`h-5 w-5 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} aria-hidden="true" />
        </span>
      </button>

      {isOpen && (
        <div id="form-access-manager-content" className="border-t border-slate-200 bg-slate-50/70 p-4 sm:p-6">
          {loadingConfigurations ? (
            <div className="flex min-h-40 items-center justify-center gap-2 text-sm text-slate-600">
              <LoaderCircle className="h-5 w-5 animate-spin" aria-hidden="true" /> Chargement des accès...
            </div>
          ) : (
            <>
            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
              <div className="border-b border-slate-200 px-4 py-4 sm:px-5">
                <h3 className="text-base font-semibold text-slate-900">
                  Formulaires administrés
                </h3>
                <p className="mt-1 text-sm text-slate-500">
                  Sélectionnez un formulaire pour gérer ses accès et ses traitants.
                </p>
                <div className="relative mt-4">
                  <Search
                    className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
                    aria-hidden="true"
                  />
                  <input
                    type="search"
                    value={formSearch}
                    onChange={(event) => setFormSearch(event.target.value)}
                    placeholder="Rechercher un formulaire..."
                    className="min-h-11 w-full rounded-lg border border-slate-300 bg-white py-2.5 pl-9 pr-3 text-sm text-slate-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    aria-label="Rechercher un formulaire"
                  />
                </div>
              </div>

              <div className="hidden grid-cols-[2.25rem_minmax(0,1fr)_9rem_12rem_1.25rem] items-center gap-3 border-b border-slate-200 bg-slate-50 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500 sm:grid">
                <span aria-hidden="true" />
                <span>Formulaire et accès</span>
                <span>État des accès</span>
                <span>Traitement</span>
                <span aria-hidden="true" />
              </div>

              {filteredForms.length === 0 ? (
                <div className="px-4 py-12 text-center">
                  <p className="text-sm font-semibold text-slate-800">
                    Aucun formulaire trouvé
                  </p>
                  <p className="mt-1 text-sm text-slate-500">
                    Modifiez les mots utilisés dans la recherche.
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-slate-200">
                  {(configuredForms.length > 0 || formSearch.trim()) && (
                    <section>
                      <div className="flex items-center justify-between bg-slate-50 px-4 py-3 sm:px-5">
                        <h4 className="text-sm font-semibold text-slate-800">
                          Accès configurés
                        </h4>
                        <span className="text-xs font-medium text-slate-500">
                          {configuredForms.length} formulaire(s)
                        </span>
                      </div>
                      {configuredForms.length > 0 ? (
                        <div className="divide-y divide-slate-200">
                          {configuredForms.map((form) =>
                            renderAdministrativeRow(form, true),
                          )}
                        </div>
                      ) : (
                        <div className="px-4 py-6 text-sm text-slate-500 sm:px-5">
                          Aucun formulaire configuré ne correspond à la recherche.
                        </div>
                      )}
                    </section>
                  )}

                  {unconfiguredForms.length > 0 && (
                    <section>
                      <button
                        type="button"
                        onClick={() => setUnconfiguredOpen((current) => !current)}
                        className="flex w-full items-center justify-between gap-3 bg-slate-50 px-4 py-3 text-left transition-colors hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500 sm:px-5"
                        aria-expanded={unconfiguredOpen || Boolean(formSearch.trim())}
                      >
                        <span>
                          <span className="block text-sm font-semibold text-slate-800">
                            À configurer
                          </span>
                          <span className="mt-0.5 block text-xs text-slate-500">
                            Formulaires qui ne possèdent encore aucun accès.
                          </span>
                        </span>
                        <span className="flex items-center gap-3">
                          <span className="text-xs font-medium text-slate-500">
                            {unconfiguredForms.length} formulaire(s)
                          </span>
                          <ChevronDown
                            className={`h-5 w-5 text-slate-500 transition-transform ${
                              unconfiguredOpen || formSearch.trim()
                                ? "rotate-180"
                                : ""
                            }`}
                            aria-hidden="true"
                          />
                        </span>
                      </button>

                      {(unconfiguredOpen || Boolean(formSearch.trim())) && (
                        <div className="divide-y divide-slate-200">
                          {unconfiguredForms.map((form) =>
                            renderAdministrativeRow(form, false),
                          )}
                        </div>
                      )}
                    </section>
                  )}
                </div>
              )}
            </div>

            {view === "detail" && (
              <div
                className="fixed inset-0 z-50 flex justify-end overscroll-none bg-slate-950/45"
                onMouseDown={(event) => {
                  if (event.target === event.currentTarget) closeFormDetails();
                }}
              >
                <section
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="form-access-details-title"
                  className="flex h-[100dvh] w-full flex-col overflow-hidden bg-white shadow-2xl sm:h-full sm:max-w-2xl sm:border-l sm:border-slate-200 lg:max-w-3xl"
                >
                  <header className="flex flex-none items-start justify-between gap-4 border-b border-slate-200 bg-white px-4 py-4 sm:px-6 sm:py-5">
                    <div className="min-w-0">
                      <h3
                        id="form-access-details-title"
                        className="break-words text-lg font-semibold text-slate-900 sm:text-xl"
                      >
                        {selectedForm.name}
                      </h3>
                      <p className="mt-1 text-sm text-slate-500">
                        {selectedForm.accessManagedSeparately
                          ? "Accès gérés séparément"
                          : `Accès : ${selectedCounts.total}`} · Traitement :{" "}
                        {selectedForm.processingGroup
                          ? `${selectedProcessingRoles.length} membre${selectedProcessingRoles.length > 1 ? "s" : ""}`
                          : "selon la demande"}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={closeFormDetails}
                      className="inline-flex h-10 w-10 flex-none items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                      aria-label="Fermer le panneau"
                      title="Fermer"
                    >
                      <X className="h-5 w-5" aria-hidden="true" />
                    </button>
                  </header>

                  <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain bg-slate-50 p-4 sm:p-6">
                    <div className="space-y-4">
                      {selectedForm.accessManagedSeparately ? (
                        <div className="flex min-h-12 items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 font-semibold text-slate-800 shadow-sm">
                          <UsersRound className="h-4 w-4 text-blue-700" aria-hidden="true" />
                          Groupe de traitement
                        </div>
                      ) : (
                        <div
                          className="grid grid-cols-2 gap-1 rounded-xl border border-slate-200 bg-white p-1.5 shadow-sm"
                          role="tablist"
                          aria-label="Gestion du formulaire"
                        >
                        <button
                          type="button"
                          role="tab"
                          aria-selected={panelSection === "formAccess"}
                          onClick={() => setPanelSection("formAccess")}
                          className={`inline-flex min-h-11 items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold ${
                            panelSection === "formAccess"
                              ? "bg-blue-700 text-white shadow-sm"
                              : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                          }`}
                        >
                          <ShieldCheck className="h-4 w-4" aria-hidden="true" />
                          Accès au formulaire
                        </button>
                        <button
                          type="button"
                          role="tab"
                          aria-selected={panelSection === "processingGroup"}
                          onClick={() => setPanelSection("processingGroup")}
                          className={`inline-flex min-h-11 items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold ${
                            panelSection === "processingGroup"
                              ? "bg-blue-700 text-white shadow-sm"
                              : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                          }`}
                        >
                          <UsersRound className="h-4 w-4" aria-hidden="true" />
                          Groupe de traitement
                        </button>
                        </div>
                      )}

                      {!selectedForm.accessManagedSeparately &&
                        panelSection === "formAccess" && (
                      <div className="space-y-4">
              {renderAddAccess()}
              {!showAddAccess && renderMessage()}

              <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
                <div className="border-b border-slate-200 px-4 pt-4 sm:px-5">
                  <h4 className="text-base font-semibold text-slate-900">Accès enregistrés</h4>
                  <div className="mt-4 flex gap-1 overflow-x-auto" role="tablist">
                    {DETAIL_TABS.map((type) => {
                      const Icon = type.icon;
                      const selected = detailTab === type.key;
                      return (
                        <button
                          key={type.key}
                          type="button"
                          role="tab"
                          aria-selected={selected}
                          onClick={() => setDetailTab(type.key)}
                          className={`inline-flex min-h-11 flex-none items-center gap-2 border-b-2 px-3 py-2 text-sm font-semibold transition-colors ${selected ? "border-blue-700 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}
                        >
                          <Icon className="h-4 w-4" aria-hidden="true" /> {type.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {detailTab === "history" ? (
                  loadingHistory ? (
                    <div className="flex min-h-40 items-center justify-center gap-2 text-sm text-slate-600">
                      <LoaderCircle className="h-5 w-5 animate-spin" />
                      Chargement de l'historique...
                    </div>
                  ) : historyError ? (
                    <div className="m-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-800">
                      <AlertCircle className="mt-0.5 h-4 w-4 flex-none" />
                      <span>{historyError}</span>
                    </div>
                  ) : historyEntries.length > 0 ? (
                    <>
                      <div className="hidden overflow-x-auto md:block">
                        <table className="w-full border-collapse text-left">
                          <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                            <tr>
                              <th className="w-48 px-5 py-3">Date et heure</th>
                              <th className="w-28 px-3 py-3">Action</th>
                              <th className="px-3 py-3">Accès concerné</th>
                              <th className="w-56 px-5 py-3">Effectué par</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-200">
                            {historyEntries.map((entry) => (
                              <tr key={entry._id}>
                                <td className="whitespace-nowrap px-5 py-3.5 text-sm text-slate-600">
                                  {formatAuditDateTime(entry.createdAt)}
                                </td>
                                <td className="px-3 py-3.5">
                                  <span
                                    className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                                      entry.action === "added"
                                        ? "bg-emerald-50 text-emerald-700"
                                        : "bg-red-50 text-red-700"
                                    }`}
                                  >
                                    {entry.action === "added" ? "Ajout" : "Retrait"}
                                  </span>
                                </td>
                                <td className="px-3 py-3.5">
                                  <p className="text-sm font-semibold text-slate-900">
                                    {entry.subjectName}
                                  </p>
                                  <p className="mt-0.5 text-xs text-slate-500">
                                    {ACCESS_TYPE_LABELS[entry.accessType] || entry.accessType}
                                  </p>
                                </td>
                                <td className="px-5 py-3.5">
                                  <p className="text-sm font-medium text-slate-800">
                                    {entry.actor?.name || "Administrateur"}
                                  </p>
                                  {entry.actor?.email && (
                                    <p className="mt-0.5 break-all text-xs text-slate-500">
                                      {entry.actor.email}
                                    </p>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      <div className="divide-y divide-slate-200 md:hidden">
                        {historyEntries.map((entry) => (
                          <div key={entry._id} className="px-4 py-4">
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <p className="break-words text-sm font-semibold text-slate-900">
                                  {entry.subjectName}
                                </p>
                                <p className="mt-1 text-xs text-slate-500">
                                  {ACCESS_TYPE_LABELS[entry.accessType] || entry.accessType}
                                </p>
                              </div>
                              <span
                                className={`inline-flex flex-none rounded-full px-2.5 py-1 text-xs font-semibold ${
                                  entry.action === "added"
                                    ? "bg-emerald-50 text-emerald-700"
                                    : "bg-red-50 text-red-700"
                                }`}
                              >
                                {entry.action === "added" ? "Ajout" : "Retrait"}
                              </span>
                            </div>
                            <p className="mt-3 text-xs text-slate-600">
                              {formatAuditDateTime(entry.createdAt)}
                            </p>
                            <p className="mt-1 text-xs text-slate-500">
                              Par {entry.actor?.name || "Administrateur"}
                            </p>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="px-4 py-12 text-center">
                      <p className="text-sm font-semibold text-slate-800">
                        Aucun changement enregistré
                      </p>
                      <p className="mt-1 text-sm text-slate-500">
                        Les prochains ajouts et retraits apparaîtront ici avec leur date et leur heure.
                      </p>
                    </div>
                  )
                ) : detailRows.length > 0 ? (
                  <div className="divide-y divide-slate-200">
                    {detailRows.map((access) => (
                      <div key={`${access.type}-${access.identifier}`} className="flex items-start gap-3 px-4 py-4 sm:items-center sm:px-5">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="break-words text-sm font-semibold text-slate-900">
                              {access.title}
                            </p>
                            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
                              {access.label}
                            </span>
                          </div>
                          {access.detail && <p className="mt-1 break-all text-xs text-slate-500">{access.detail}</p>}
                        </div>
                        <button
                          type="button"
                          onClick={() => removeAccess(access.type, access.identifier)}
                          disabled={saving}
                          className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-red-50 hover:text-red-700 disabled:cursor-wait disabled:opacity-50"
                          aria-label={`Retirer ${access.title}`}
                          title="Retirer cet accès"
                        >
                          {saving ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" aria-hidden="true" />}
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="px-4 py-12 text-center">
                    <p className="text-sm font-semibold text-slate-800">Aucun accès dans cette catégorie</p>
                    <p className="mt-1 text-sm text-slate-500">Utilisez « Ajouter un accès » pour créer la première autorisation.</p>
                  </div>
                )}
                        </div>
                      </div>
                      )}

                      {panelSection === "processingGroup" && (
                      <div className="space-y-4">
                      {selectedForm.processingGroup ? (
                        <>
                            <div className="rounded-xl border border-blue-200 bg-blue-50/40 p-4 sm:p-5">
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <h4 className="text-base font-semibold text-slate-900">
                                    Ajouter un membre traitant
                                  </h4>
                                  <p className="mt-1 text-sm text-slate-600">
                                    Recherchez une personne à ajouter au groupe.
                                  </p>
                                </div>
                                <button
                                  type="button"
                                  onClick={() =>
                                    setShowAddProcessingMember((current) => !current)
                                  }
                                  className="flex-none text-sm font-semibold text-blue-700 hover:text-blue-900"
                                  aria-expanded={showAddProcessingMember}
                                >
                                  {showAddProcessingMember ? "Réduire" : "Afficher"}
                                </button>
                              </div>

                              {showAddProcessingMember && (
                              <>
                              <div className="mt-4 grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
                                <div className="min-w-0">
                                  <label className="mb-1.5 block text-sm font-semibold text-slate-800">
                                    Utilisateur
                                  </label>
                                  <UsersList
                                    key={`${selectedFormKey}-processing-${processingUserSearchKey}`}
                                    onUserSelect={setSelectedProcessingUser}
                                    initialValue={
                                      selectedProcessingUser?.mail ||
                                      selectedProcessingUser?.userPrincipalName ||
                                      ""
                                    }
                                    isRequired={false}
                                    placeholder="Nom ou courriel"
                                    inputClassName="min-h-11 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-800 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                                  />
                                  {selectedProcessingUser && (
                                    <p className="mt-2 text-xs text-slate-600">
                                      {selectedProcessingUser.displayName}
                                      {selectedProcessingUser.jobTitle
                                        ? ` · ${selectedProcessingUser.jobTitle}`
                                        : ""}
                                    </p>
                                  )}
                                </div>
                                <button
                                  type="button"
                                  onClick={addProcessingMember}
                                  disabled={!selectedProcessingUser || processingSaving}
                                  className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50 lg:w-auto"
                                >
                                  {processingSaving ? (
                                    <LoaderCircle className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Plus className="h-4 w-4" />
                                  )}
                                  Ajouter
                                </button>
                              </div>
                              </>
                              )}
                            </div>

                          {processingMessage && (
                            <div
                              className={`flex items-start gap-2 rounded-lg border px-3 py-3 text-sm ${
                                processingMessage.type === "success"
                                  ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                                  : "border-red-200 bg-red-50 text-red-800"
                              }`}
                              role={processingMessage.type === "error" ? "alert" : "status"}
                            >
                              {processingMessage.type === "success" ? (
                                <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none" />
                              ) : (
                                <AlertCircle className="mt-0.5 h-4 w-4 flex-none" />
                              )}
                              <span>{processingMessage.text}</span>
                            </div>
                          )}

                          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
                            <div className="border-b border-slate-200 px-4 py-4 sm:px-5">
                              <h4 className="text-base font-semibold text-slate-900">
                                Membres du groupe de traitement
                              </h4>
                              <p className="mt-1 text-sm text-slate-500">
                                {selectedForm.processingGroup} · {selectedProcessingRoles.length} membre{selectedProcessingRoles.length > 1 ? "s" : ""}
                              </p>
                            </div>
                            {selectedProcessingRoles.length > 0 ? (
                              <Grouproleschema
                                roles={selectedProcessingRoles}
                                groupName={selectedForm.processingGroup}
                                onRoleDeleted={onProcessingRoleDeleted}
                                isOpen
                                onToggle={() => {}}
                                showGroupHeader={false}
                              />
                            ) : (
                              <div className="px-4 py-12 text-center sm:px-5">
                                <p className="text-sm font-semibold text-slate-800">
                                  Aucun membre traitant
                                </p>
                                <p className="mt-1 text-sm text-slate-500">
                                  Utilisez « Ajouter un membre » pour compléter ce groupe.
                                </p>
                              </div>
                            )}
                          </div>
                        </>
                      ) : (
                        <div className="rounded-xl border border-slate-200 bg-white px-4 py-12 text-center sm:px-6">
                          <UsersRound className="mx-auto h-8 w-8 text-slate-400" aria-hidden="true" />
                          <p className="mt-3 text-sm font-semibold text-slate-800">
                            Traitement géré dans la demande
                          </p>
                          <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">
                            Ce formulaire n’utilise pas un groupe de traitement général. Les responsables sont déterminés selon le processus de chaque demande.
                          </p>
                        </div>
                      )}
                      </div>
                      )}
                    </div>
                  </div>
                  </section>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
};

export default FormAccessManager;
